import React, { useState, useEffect } from 'react';
import {
    Card,
    Text,
    Button,
    Badge,
    Toast,
    ToastTitle,
    ToastBody,
    Toaster,
    useToastController,
} from '@fluentui/react-components';
import {
    Warning24Regular,
    Alert24Regular,
    Dismiss24Regular,
    Shield24Regular,
    Heart24Regular,
    Important24Regular,
} from '@fluentui/react-icons';

interface Alert {
    id: string;
    type: 'crisis' | 'violence' | 'medication' | 'risk' | 'safety';
    severity: 'low' | 'medium' | 'high' | 'critical';
    title: string;
    message: string;
    keyword?: string;
    timestamp: string;
    acknowledged: boolean;
    actionRequired: boolean;
}

interface RealTimeAlertsProps {
    consultationId: string;
    alerts: Alert[];
    onAcknowledgeAlert: (alertId: string) => void;
    onTakeAction: (alertId: string, action: string) => void;
    isVisible: boolean;
}

export const RealTimeAlerts: React.FC<RealTimeAlertsProps> = ({
    consultationId,
    alerts,
    onAcknowledgeAlert,
    onTakeAction,
    isVisible,
}) => {
    const [visibleAlerts, setVisibleAlerts] = useState<Alert[]>([]);
    const { dispatchToast } = useToastController();

    useEffect(() => {
        // Filter for unacknowledged alerts
        const unacknowledged = alerts.filter((alert) => !alert.acknowledged);
        setVisibleAlerts(unacknowledged);

        // Show toast for critical alerts
        unacknowledged.forEach((alert) => {
            if (alert.severity === 'critical' || alert.severity === 'high') {
                showCriticalAlertToast(alert);
            }
        });
    }, [alerts]);

    const showCriticalAlertToast = (alert: Alert) => {
        dispatchToast(
            <Toast>
                <ToastTitle>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {getAlertIcon(alert.type, alert.severity)}
                        {alert.title}
                    </div>
                </ToastTitle>
                <ToastBody>{alert.message}</ToastBody>
            </Toast>,
            {
                intent: alert.severity === 'critical' ? 'error' : 'warning',
                timeout: -1, // Don't auto-dismiss critical alerts
            }
        );
    };

    const getAlertIcon = (type: string, severity: string) => {
        const iconStyle = {
            fontSize: '20px',
            color: getSeverityColor(severity),
        };

        switch (type) {
            case 'crisis':
                return <Alert24Regular style={iconStyle} />;
            case 'violence':
                return <Warning24Regular style={iconStyle} />;
            case 'safety':
                return <Shield24Regular style={iconStyle} />;
            case 'medication':
                return <Heart24Regular style={iconStyle} />;
            default:
                return <Important24Regular style={iconStyle} />;
        }
    };

    const getSeverityColor = (severity: string) => {
        switch (severity) {
            case 'critical':
                return '#8b0000';
            case 'high':
                return '#d13438';
            case 'medium':
                return '#ffa500';
            case 'low':
                return '#107c10';
            default:
                return '#605e5c';
        }
    };

    const getSeverityBadge = (severity: string) => {
        switch (severity) {
            case 'critical':
                return (
                    <Badge appearance="filled" color="danger">
                        Critical
                    </Badge>
                );
            case 'high':
                return (
                    <Badge appearance="filled" color="danger">
                        High
                    </Badge>
                );
            case 'medium':
                return (
                    <Badge appearance="tint" color="warning">
                        Medium
                    </Badge>
                );
            case 'low':
                return (
                    <Badge appearance="outline" color="success">
                        Low
                    </Badge>
                );
            default:
                return <Badge appearance="outline">Unknown</Badge>;
        }
    };

    const handleAcknowledge = (alertId: string) => {
        onAcknowledgeAlert(alertId);
        setVisibleAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
    };

    const getActionButtons = (alert: Alert) => {
        const actions = [];

        switch (alert.type) {
            case 'crisis':
                actions.push(
                    <Button
                        key="crisis-protocol"
                        appearance="primary"
                        size="small"
                        onClick={() => onTakeAction(alert.id, 'crisis_protocol')}
                        style={{ backgroundColor: '#d13438' }}
                    >
                        Activate Crisis Protocol
                    </Button>
                );
                actions.push(
                    <Button
                        key="safety-plan"
                        appearance="outline"
                        size="small"
                        onClick={() => onTakeAction(alert.id, 'safety_plan')}
                    >
                        Safety Plan
                    </Button>
                );
                break;

            case 'violence':
                actions.push(
                    <Button
                        key="security"
                        appearance="primary"
                        size="small"
                        onClick={() => onTakeAction(alert.id, 'contact_security')}
                        style={{ backgroundColor: '#d13438' }}
                    >
                        Contact Security
                    </Button>
                );
                break;

            case 'safety':
                actions.push(
                    <Button
                        key="supervisor"
                        appearance="outline"
                        size="small"
                        onClick={() => onTakeAction(alert.id, 'notify_supervisor')}
                    >
                        Notify Supervisor
                    </Button>
                );
                break;

            case 'medication':
                actions.push(
                    <Button
                        key="review-meds"
                        appearance="outline"
                        size="small"
                        onClick={() => onTakeAction(alert.id, 'review_medications')}
                    >
                        Review Medications
                    </Button>
                );
                break;
        }

        return actions;
    };

    if (!isVisible || visibleAlerts.length === 0) {
        return <Toaster />;
    }

    return (
        <div
            style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                width: '400px',
                maxHeight: '500px',
                overflowY: 'auto',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
            }}
        >
            {visibleAlerts.map((alert) => (
                <Card
                    key={alert.id}
                    style={{
                        padding: '16px',
                        border: `2px solid ${getSeverityColor(alert.severity)}`,
                        backgroundColor: alert.severity === 'critical' ? '#ffeaea' : '#ffffff',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '12px',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {getAlertIcon(alert.type, alert.severity)}
                            <Text size={500} weight="semibold">
                                {alert.title}
                            </Text>
                            {getSeverityBadge(alert.severity)}
                        </div>
                        <Button
                            appearance="subtle"
                            size="small"
                            icon={<Dismiss24Regular />}
                            onClick={() => handleAcknowledge(alert.id)}
                        />
                    </div>

                    <Text size={400} style={{ marginBottom: '12px', color: '#605e5c' }}>
                        {alert.message}
                    </Text>

                    {alert.keyword && (
                        <Text
                            size={300}
                            style={{ marginBottom: '12px', fontStyle: 'italic', color: '#605e5c' }}
                        >
                            Triggered by: "{alert.keyword}"
                        </Text>
                    )}

                    <Text size={300} style={{ marginBottom: '16px', color: '#8b8b8b' }}>
                        {new Date(alert.timestamp).toLocaleTimeString()}
                    </Text>

                    {alert.actionRequired && (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {getActionButtons(alert)}
                        </div>
                    )}

                    {!alert.actionRequired && (
                        <Button
                            appearance="outline"
                            size="small"
                            onClick={() => handleAcknowledge(alert.id)}
                        >
                            Acknowledge
                        </Button>
                    )}
                </Card>
            ))}
            <Toaster />
        </div>
    );
};
